import { t, formatDate } from "../core/i18n.js";
import { runtime } from "../core/runtime.js";
import { icon } from "./icons.js";
import {
  compFor,
  dateKey,
  esc,
  examDateLabel,
  examDaysLeft,
  groupLabel,
  metrics,
  nextExam,
  planGroup,
  tasksFor,
  weekday,
} from "../core/model.js";

const copy = (key, values) => esc(t(key, values));
// Circumference of the progress ring (r = 36).
const RING = 226.195;

export function renderDashboard(isToday, taskHTML, timer) {
  const date = runtime.selectedDate;
  const key = dateKey(date);
  const progress = metrics(date);
  const tasks = tasksFor(date);
  const completed = compFor(key);
  const future = key > dateKey(new Date());
  const percent = Math.round(progress.pct);

  return `<div class="kun-dash">
    <section class="kun-sheet" aria-labelledby="kunDayTitle">
      <header class="kun-heading"><h1 id="kunDayTitle">${esc(formatDate(date, { day: "numeric", month: "long" }))}<small>${date.getFullYear()}</small></h1><div class="kun-heading-meta"><span>${esc(isToday ? t("Today") : weekday(date))} · ${esc(groupLabel(planGroup(date)))}</span><button class="kun-link" data-page="manage">${copy("Manage Plans")}${icon("arrow")}</button></div></header>
      ${future ? `<p class="kun-note">${icon("clock")}${copy("Future days are available to view; completion opens on the day.")}</p>` : ""}
      ${tasks.length ? `<div class="task-list kun-tasks" aria-label="${copy(isToday ? "Today’s plan" : "Daily plans")}">${tasks.map((task, index) => taskHTML(task, completed[task.id], index)).join("")}</div>` : `<div class="kun-empty"><h2>${copy("Your first plan starts here")}</h2><p>${copy("Add your priorities for this day. Check them off as you go.")}</p></div>`}
      <button class="kun-add" ${tasks.length ? 'data-action="add"' : `data-create-plan="${esc(planGroup(date))}"`}>${icon("plus")}${copy("Add plan")}</button>
    </section>
    <aside class="kun-analytics" aria-label="${copy("Focus and upcoming dates")}">
      <div class="kun-completion">
        <div class="kun-ring" role="progressbar" aria-label="${copy("Daily progress")}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}" aria-valuetext="${copy("{done} of {total} plans completed", { done: progress.done, total: progress.total })} — ${percent}%"><svg viewBox="0 0 88 88" aria-hidden="true"><circle class="kun-ring-track" cx="44" cy="44" r="36"/><circle class="kun-ring-value" cx="44" cy="44" r="36" style="stroke-dashoffset:${(RING * (1 - progress.pct / 100)).toFixed(3)}"/></svg><span aria-hidden="true">${percent}%</span></div>
        <div class="kun-legend"><div><span>${copy("Total plans")}</span><strong>${progress.total}</strong></div><div><span><i aria-hidden="true"></i>${copy("Completed")}</span><strong>${progress.done}</strong></div><div><span><i aria-hidden="true"></i>${copy("Remaining")}</span><strong>${progress.total - progress.done}</strong></div></div>
      </div>
      ${renderDashboardWeek(date)}
      ${renderFocus(timer)}
      ${renderExam()}
    </aside>
  </div>`;
}

function lastSevenDays() {
  const today = new Date();
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setHours(12, 0, 0, 0);
    date.setDate(today.getDate() - 6 + index);
    return { date, key: dateKey(date), ...metrics(date) };
  });
  const max = Math.max(
    2,
    Math.ceil(Math.max(...days.map((day) => day.total)) / 2) * 2,
  );
  return { today, days, max };
}

// Checking a plan updates the numbers in place, so the strike-through, the
// ring and the bars animate instead of the whole page being redrawn.
export function updateDashboardProgress() {
  const dash = document.querySelector(".kun-dash");
  if (!dash) return false;
  const progress = metrics(runtime.selectedDate);
  const percent = Math.round(progress.pct);
  const ring = dash.querySelector(".kun-ring");
  if (ring) {
    ring.setAttribute("aria-valuenow", String(percent));
    ring.setAttribute(
      "aria-valuetext",
      `${t("{done} of {total} plans completed", { done: progress.done, total: progress.total })} — ${percent}%`,
    );
    ring.querySelector(".kun-ring-value").style.strokeDashoffset = (
      RING *
      (1 - progress.pct / 100)
    ).toFixed(3);
    ring.querySelector(":scope > span").textContent = `${percent}%`;
  }
  const values = dash.querySelectorAll(".kun-legend strong");
  [progress.total, progress.done, progress.total - progress.done].forEach(
    (value, index) => {
      if (values[index]) values[index].textContent = value;
    },
  );
  const { days, max } = lastSevenDays();
  const axis = dash.querySelectorAll(".kun-axis span");
  if (axis.length === 3) {
    axis[0].textContent = max;
    axis[1].textContent = max / 2;
  }
  for (const day of days) {
    const bar = dash.querySelector(`.kun-bar[data-open-date="${day.key}"]`);
    if (!bar) continue;
    const pct = Math.round(day.pct);
    const full = formatDate(day.date, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    bar.querySelector(".kun-bar-column").style.height =
      `${((day.total / max) * 70).toFixed(1)}px`;
    bar.querySelector(".kun-bar-column i").style.height =
      `${day.total ? (day.done / day.total) * 100 : 0}%`;
    bar.setAttribute(
      "aria-label",
      `${full}: ${t("{done} of {total} plans completed", day)}, ${pct}%`,
    );
    const tip = bar.querySelectorAll(".kun-tooltip > span");
    if (tip.length === 2) {
      tip[0].textContent = t("{done} of {total} plans completed", day);
      tip[1].textContent = t("{count} remaining", {
        count: day.total - day.done,
      });
    }
    const label = bar.querySelector(".kun-tooltip b");
    if (label) label.textContent = `${pct}%`;
  }
  return true;
}

// Completion for the last seven days, ending today, in the same form as the daily ring.
export function renderDashboardWeek(selected) {
  const { today, days, max } = lastSevenDays();
  const todayKey = dateKey(today);
  const yesterdayKey = days[5].key;
  const selectedKey = dateKey(selected);
  return `<section class="kun-history" aria-labelledby="kunHistoryTitle"><div class="kun-section-head"><h2 id="kunHistoryTitle">${copy("By day")}</h2><span>${copy("Last {count} days", { count: 7 })}</span></div><div class="kun-plot"><div class="kun-axis" aria-hidden="true"><span>${max}</span><span>${max / 2}</span><span>0</span></div><div class="kun-bars">${days
    .map((day) => {
      const label =
        day.key === todayKey
          ? t("Today")
          : day.key === yesterdayKey
            ? t("Yesterday")
            : `${day.date.getDate()}/${day.date.getMonth() + 1}`;
      const pct = Math.round(day.pct);
      const full = formatDate(day.date, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return `<button type="button" class="kun-bar" data-open-date="${day.key}" ${day.key === selectedKey ? 'aria-current="date"' : ""} aria-label="${esc(full)}: ${copy("{done} of {total} plans completed", day)}, ${pct}%"><span class="kun-bar-column" style="height:${((day.total / max) * 70).toFixed(1)}px"><i style="height:${day.total ? (day.done / day.total) * 100 : 0}%"></i></span><span class="kun-bar-label">${esc(label)}</span><span class="kun-tooltip" aria-hidden="true"><strong>${esc(full)}</strong><span>${copy("{done} of {total} plans completed", day)}</span><span>${copy("{count} remaining", { count: day.total - day.done })}</span><b>${pct}%</b></span></button>`;
    })
    .join("")}</div></div></section>`;
}

function renderFocus(timer) {
  const running = runtime.focusTimer?.running;
  const paused = runtime.focusTimer?.paused;
  const rest = runtime.focusTimer?.mode === "rest";
  const status = running
    ? rest
      ? "Break in progress"
      : "Focus in progress"
    : paused
      ? "Paused"
      : "Ready to focus";
  return `<section class="kun-focus${running ? " running" : ""}" aria-labelledby="kunFocusTitle"><div class="kun-section-head"><h2 id="kunFocusTitle">${copy("Focus timer")}</h2><span class="kun-status"><i aria-hidden="true"></i>${copy(status)}</span></div><div class="kun-timer" id="miniTimer" role="timer" aria-label="${copy("Focus timer")}">${esc(timer)}</div><span class="kun-timer-mode">${copy(rest ? "Rest" : "Work")} · ${copy("Pomodoro")}</span><button class="kun-primary" data-action="pomStart">${icon(running ? "pause" : "play")}${copy(running ? "Pause focus timer" : paused ? "Resume focus" : "Start focus timer")}</button><button class="kun-link" data-page="pomodoro">${copy("Timer settings")}${icon("arrow")}</button></section>`;
}

function renderExam() {
  const exam = nextExam();
  if (!exam)
    return `<section class="kun-exam" aria-labelledby="kunExamTitle"><div class="kun-section-head"><h2 id="kunExamTitle">${copy("Upcoming exam")}</h2></div><p class="kun-exam-date">${copy("No upcoming exams")}</p><button class="kun-link" data-add-exam>${copy("Add exam date")}${icon("plus")}</button></section>`;
  const days = Math.max(0, examDaysLeft(exam.date));
  return `<section class="kun-exam" aria-labelledby="kunExamTitle"><div class="kun-section-head"><h2 id="kunExamTitle">${copy("Upcoming exam")}</h2><span>${copy(days === 0 ? "EXAM DAY" : "DAYS LEFT")}</span></div><div class="kun-exam-row"><div><strong class="kun-exam-name">${esc(exam.name)}</strong><span class="kun-exam-date">${esc(examDateLabel(exam.date))}</span></div><b class="kun-exam-count">${days}</b></div><button class="kun-link" data-open-exams>${copy("View exam dates")}${icon("arrow")}</button></section>`;
}
